import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, ClipboardCheck, BellRing } from "lucide-react";
import { PageHeader, Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

function Row({ r, onDecide }) {
  const [reason, setReason] = useState("");
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`rbr-queue-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-head text-[14px] font-bold text-slate-900">{r.customer} · {r.payee}</div>
          <div className="text-[12px] text-slate-500">{r.type_label} · ${r.monthly_amount}/mo · {r.payments.length} payment(s) logged · method: {r.verification?.method || "—"}</div>
          {r.verification?.evidence_note && <div className="mt-1 text-[12px] italic text-slate-500">"{r.verification.evidence_note}"</div>}
        </div>
        <span className="rounded-full bg-amber-50 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-700">{r.stage.replace("_", " ")}</span>
      </div>
      {r.stage === "verification_pending" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required for exception)" data-testid="rbr-decide-reason" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
          <button onClick={() => onDecide(r.id, "verified", reason)} data-testid="rbr-verify-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-[12px] font-bold text-white hover:bg-emerald-700"><CheckCircle2 size={14} /> Verify</button>
          <button onClick={() => onDecide(r.id, "exception", reason)} data-testid="rbr-exception-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-[12px] font-bold text-red-600 hover:bg-red-50"><XCircle size={14} /> Exception</button>
        </div>
      )}
    </div>
  );
}

export default function RentBillQueue() {
  const [data, setData] = useState(null);
  const [rem, setRem] = useState(null);
  const load = useCallback(() => {
    api.get("/owner/rent-bill/queue").then(({ data }) => setData(data));
    api.get("/owner/rent-bill/reminders").then(({ data }) => setRem(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const runReminders = async () => {
    try { await api.post("/owner/rent-bill/reminders/run"); toast.success("Reminder run started"); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run reminders."); }
  };
  const decide = async (id, decision, reason) => {
    try { await api.post(`/owner/rent-bill/${id}/decide`, { decision, reason }); toast.success(`Marked ${decision}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not decide."); }
  };
  if (!data) return <Loading />;
  const c = data.counts;
  return (
    <div className="animate-fade-up space-y-6" data-testid="rbr-queue-page">
      <PageHeader eyebrow="Customers & Finance" title="Rent & Bill Verification Queue"
        subtitle="Human review of member payment evidence. Verified history waits for a connected furnisher — MMT never marks anything reported itself." />
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Pending review" value={c.verification_pending} tone={c.verification_pending ? "red" : "slate"} />
        <Stat label="Verified" value={c.verified + c.awaiting_provider} tone="green" />
        <Stat label="Awaiting provider" value={c.awaiting_provider} sub={data.furnisher_live ? "Furnisher live" : "No furnisher connected"} />
        <Stat label="Exceptions" value={c.exception} />
      </div>
      <Card className="p-5" data-testid="rbr-reminders-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-head text-[15px] font-bold text-slate-900"><BellRing size={16} className="text-[#b0862f]" /> Monthly verification reminders</div>
            <p className="mt-0.5 text-[12px] text-slate-500">Cron: {rem?.schedule || "1st of each month"}. Emails members whose enrolled payments have a gap for last month or still need verification. Opted-out members are skipped.</p>
          </div>
          <button onClick={runReminders} data-testid="rbr-run-reminders-btn" className="rounded-lg bg-[#080d1a] px-4 py-2 font-head text-[12px] font-bold text-white hover:bg-[#1a5fd0]">Run now</button>
        </div>
        {rem && (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Would be nudged now ({rem.pending.length})</div>
              {rem.pending.length === 0 ? <div className="mt-1 text-[12px] text-slate-400">No gaps — everyone is current.</div> : rem.pending.map((p) => (
                <div key={p.user_id} className="mt-1 text-[12px] text-slate-700" data-testid="rbr-reminder-pending"><b>{p.email}</b> · {p.items.map((i) => i.payee).join(", ")}</div>
              ))}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Recent runs</div>
              {rem.recent_runs.length === 0 ? <div className="mt-1 text-[12px] text-slate-400">No runs yet.</div> : rem.recent_runs.map((r) => (
                <div key={r.run_id} className="mt-1 font-mono text-[11px] text-slate-600" data-testid="rbr-reminder-run">{String(r.at).slice(0, 16).replace("T", " ")} · {r.sent} sent</div>
              ))}
            </div>
          </div>
        )}
      </Card>
      <div>
        <div className="mb-2 flex items-center gap-2 font-head text-[15px] font-bold text-slate-900"><ClipboardCheck size={16} className="text-[#1a5fd0]" /> All enrollments</div>
        {data.enrollments.length === 0 ? <Card className="p-6 text-center text-[13px] text-slate-500" data-testid="rbr-queue-empty">No member enrollments yet.</Card>
          : <div className="space-y-3">{data.enrollments.map((r) => <Row key={r.id} r={r} onDecide={decide} />)}</div>}
      </div>
    </div>
  );
}
